import * as React from "react";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { BrokerAccountRef } from "@/utils/brokerAccountRef";
import {
  brokerAccountRefKey,
  formatBrokerAccountRefLabel,
} from "@/utils/brokerAccountRef";

export type BrokerAccountsMultiSelectProps = {
  options: BrokerAccountRef[];
  selected: BrokerAccountRef[];
  onChange: (next: BrokerAccountRef[]) => void;
  /** Empty selection trigger label. */
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
};

/**
 * Multi-select for `{ brokerName, brokerAccountCode }` — strategies + policy account tags.
 */
export function BrokerAccountsMultiSelect({
  options,
  selected,
  onChange,
  emptyLabel = "Choose accounts…",
  disabled = false,
  className,
}: BrokerAccountsMultiSelectProps): React.ReactElement {
  const selectedKeys = React.useMemo(
    () => new Set(selected.map(brokerAccountRefKey)),
    [selected]
  );

  const triggerLabel =
    selected.length === 0
      ? emptyLabel
      : selected.length === 1
        ? formatBrokerAccountRefLabel(selected[0]!)
        : `${selected.length} accounts selected`;

  const toggle = (option: BrokerAccountRef, checked: boolean) => {
    const key = brokerAccountRefKey(option);
    if (checked) {
      if (selectedKeys.has(key)) return;
      onChange([...selected, option]);
      return;
    }
    onChange(selected.filter((item) => brokerAccountRefKey(item) !== key));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline-gray"
          size="sm"
          disabled={disabled || options.length === 0}
          data-font="primary"
          className={cn(
            "h-8 w-fit max-w-full min-w-[10rem] justify-between gap-2 font-normal",
            className
          )}
          title={
            selected.length > 0
              ? selected.map(formatBrokerAccountRefLabel).join(", ")
              : undefined
          }
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-auto min-w-[var(--radix-popover-trigger-width)] max-w-sm gap-0 p-2"
        data-broker-accounts-multi-select=""
      >
        <div className="flex max-h-56 flex-col gap-1 overflow-y-auto">
          {options.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">No accounts available</p>
          ) : (
            options.map((option) => {
              const key = brokerAccountRefKey(option);
              const checkboxId = `broker-account-${key}`;
              const isChecked = selectedKeys.has(key);
              return (
                <label
                  key={key}
                  htmlFor={checkboxId}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent/50"
                >
                  <Checkbox
                    id={checkboxId}
                    checked={isChecked}
                    onCheckedChange={(checked) => toggle(option, checked === true)}
                  />
                  <span className="min-w-0 truncate text-sm">
                    {formatBrokerAccountRefLabel(option)}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
